import { makeStyles } from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import React, { memo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { capitalize, lowerCase } from "lodash";
import { notify } from "../../../actions/notification";
import { Save, DeleteForever } from "../../../icons";
import { saveSuppressionList } from "../../../actions/suppression";
import {
  Page,
  Box,
  Grid,
  TextField,
  MenuItem,
  PhoneInput,
  Card,
  CardContent,
  Typography,
  ErrorButtonContained,
  PrimaryButtonContained,
} from "../../../components";
import SuppressionToggle from "../SuppressionToggle";
import Upload from "./Upload/File/Upload/index";
import { globalClasses } from "../../../components/ThemeProvider";
import { onInputTabChange, SUPPRESSION_TYPE_ENUM } from "../util";

const useStyles = makeStyles((theme) => ({
  content: {
    width: "100%",
    overflow: "auto",
  },
  root: {
    padding: `0 ${theme.spacing(2)}px`,
    backgroundColor: theme.palette.grey[200],
    marginTop: "auto",
    marginBottom: 0,
  },
}));

function CreateSuppressionNumber({
  type,
  shortCodes = [],
  domainAddresses = [],
  callerIds = [],
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [isUpload, setIsUpload] = useState(false);
  const { errors = {} } = useSelector((state) =>
    state.suppression ? state.suppression : {}
  );

  const suppressionList = useSelector((state) => {
    return state.newSuppressionList;
  });

  const [suppressNumber, setSuppressNumber] = useState({});
  const [suppressEmail, setSuppressEmail] = useState({
    toEmail: "",
    fromEmail: "",
  });
  const [suppressVoice, setSuppressVoice] = useState({ callerId: "", to: "" });

  const { shortCode = "", to } = suppressNumber;

  const onChange = (ev) => {
    const { name, value } = ev.target;
    switch (type) {
      case SUPPRESSION_TYPE_ENUM.SMS:
        {
          setSuppressNumber({
            ...suppressNumber,
            [name]: value?.toString(),
          });
        }
        break;
      case SUPPRESSION_TYPE_ENUM.EMAIL:
        {
          setSuppressEmail({
            ...suppressEmail,
            [name]: value,
          });
        }
        break;
      case SUPPRESSION_TYPE_ENUM.VOICE: {
        setSuppressVoice({
          ...suppressVoice,
          [name]: value,
        });
      }
    }
  };

  const onSave = useCallback(async () => {
    try {
      if (type === SUPPRESSION_TYPE_ENUM.SMS) {
        if (suppressionList?.preview?.length > 0) {
          await dispatch(saveSuppressionList(type, suppressionList.preview));
        } else {
          await dispatch(
            saveSuppressionList(type, [{ to, shortcode: shortCode }])
          );
        }
      } else if (type === SUPPRESSION_TYPE_ENUM.EMAIL) {
        if (suppressionList?.preview?.length > 0) {
          await dispatch(saveSuppressionList(type, suppressionList.preview));
        } else {
          await dispatch(
            saveSuppressionList(type, [
              {
                to: suppressEmail.toEmail,
                domainAddress: suppressEmail.fromEmail,
              },
            ])
          );
        }
      } else if (type === SUPPRESSION_TYPE_ENUM.VOICE) {
        if (suppressionList?.preview?.length > 0) {
          await dispatch(saveSuppressionList(type, suppressionList.preview));
        } else {
          await dispatch(
            saveSuppressionList(type, [
              {
                to: suppressVoice.to,
                callerId: suppressVoice.callerId,
              },
            ])
          );
        }
      }

      history.replace(`/suppression?type=${type}`);
    } catch (err) {
      dispatch(
        notify({
          type: "error",
          message: err.message,
        })
      );
    }
  }, [
    dispatch,
    to,
    shortCode,
    history,
    suppressEmail,
    suppressVoice,
    suppressionList,
    type,
  ]);

  const onCancel = useCallback(
    () => history.replace(`/suppression?type=${type}`),
    [history]
  );

  const tabLabels = {
    [SUPPRESSION_TYPE_ENUM.SMS]: "SMS", // special uppercase exception
  };

  const label = tabLabels[type]
    ? tabLabels[type]
    : type
    ? capitalize(lowerCase(type))
    : "";
  const addSMSToSuppressionList = (
    <>
      <Grid item xs={12} sm={12} md={6}>
        <TextField
          data-test-id="suppression-create-shortcode-field"
          select
          label={t("Short Code")}
          variant="outlined"
          size="small"
          fullWidth
          name="shortCode"
          value={shortCode} // todo
          onChange={onChange} // todo
          error={Boolean(
            errors[SUPPRESSION_TYPE_ENUM.SMS] &&
              errors[SUPPRESSION_TYPE_ENUM.SMS]["shortcode"]
          )}
          helperText={
            Boolean(
              errors[SUPPRESSION_TYPE_ENUM.SMS] &&
                errors[SUPPRESSION_TYPE_ENUM.SMS]["shortcode"]
            )
              ? t("must be a valid shortcode")
              : ""
          }
          className={globalClasses.filterPaper}
        >
          {shortCodes.map((shortCode) => (
            <MenuItem key={shortCode} value={shortCode}>
              {shortCode}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={12} md={6}>
        <PhoneInput
          data-test-id="suppression-create-sms-number-field"
          label={t("Phone Number")}
          country={"us"}
          value={to}
          name="to"
          onChange={(value) => onChange({ target: { name: "to", value } })}
          variant="outlined"
          size="small"
          fullWidth
          error={Boolean(
            errors[SUPPRESSION_TYPE_ENUM.SMS] &&
              errors[SUPPRESSION_TYPE_ENUM.SMS]["to"]
          )}
          helperText={
            Boolean(
              errors[SUPPRESSION_TYPE_ENUM.SMS] &&
                errors[SUPPRESSION_TYPE_ENUM.SMS]["to"]
            )
              ? t("must be a valid phone number")
              : ""
          }
        />
      </Grid>
      {/* ECS-2200 Commented out to remove this feature while being investigated */}
      {/*<Grid item xs={12} sm={6} md={4} lg={3} xl={2}>*/}
      {/*  <TextField*/}
      {/*    data-test-id="suppression-upload"*/}
      {/*    size="small"*/}
      {/*    name="toEmail"*/}
      {/*    value={suppressEmail.toEmail}*/}
      {/*    onChange={() => setIsUpload(true)}*/}
      {/*    helperText={*/}
      {/*      Boolean(errors["toEmail"]) ? t("must be a valid Email") : ""*/}
      {/*    }*/}
      {/*    type="checkbox"*/}
      {/*  />{" "}*/}
      {/*  Uploads*/}
      {/*</Grid>*/}
    </>
  );
  const addEmailToSuppressionList = (
    <>
      <Grid item xs={12} sm={12} md={6}>
        <TextField
          data-test-id="suppression-create-from-email"
          select
          label={t("From Email")}
          variant="outlined"
          size="small"
          fullWidth
          name="fromEmail"
          value={suppressEmail.fromEmail}
          onChange={onChange}
          error={Boolean(
            errors[SUPPRESSION_TYPE_ENUM.EMAIL] &&
              errors[SUPPRESSION_TYPE_ENUM.EMAIL]["domainAddress"]
          )}
          helperText={
            Boolean(
              errors[SUPPRESSION_TYPE_ENUM.EMAIL] &&
                errors[SUPPRESSION_TYPE_ENUM.EMAIL]["domainAddress"]
            )
              ? t("must be a valid Email")
              : ""
          }
          className={globalClasses.filterPaper}
        >
          {domainAddresses.map((domain) => (
            <MenuItem key={domain} value={domain}>
              {domain}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <TextField
          data-test-id="suppression-create-to-email"
          label={t("To Email")}
          variant="outlined"
          size="small"
          fullWidth
          name="toEmail"
          value={suppressEmail.toEmail}
          onChange={onChange}
          error={Boolean(
            errors[SUPPRESSION_TYPE_ENUM.EMAIL] &&
              errors[SUPPRESSION_TYPE_ENUM.EMAIL]["to"]
          )}
          helperText={
            Boolean(
              errors[SUPPRESSION_TYPE_ENUM.EMAIL] &&
                errors[SUPPRESSION_TYPE_ENUM.EMAIL]["to"]
            )
              ? t("must be a valid Email")
              : ""
          }
          className={globalClasses.filterPaper}
        />
      </Grid>
      {/* ECS-2200 Commented out to remove this feature while being investigated */}
      {/*<Grid item xs={12} sm={6} md={4} lg={3} xl={2}>*/}
      {/*  <TextField*/}
      {/*    data-test-id="suppression-upload"*/}
      {/*    size="small"*/}
      {/*    name="toEmail"*/}
      {/*    value={suppressEmail.toEmail}*/}
      {/*    onChange={() => setIsUpload(true)}*/}
      {/*    helperText={*/}
      {/*      Boolean(errors["toEmail"]) ? t("must be a valid Email") : ""*/}
      {/*    }*/}
      {/*    type="checkbox"*/}
      {/*  />{" "}*/}
      {/*  Uploads*/}
      {/*</Grid>*/}
    </>
  );
  const addVoiceToSuppressionList = (
    <>
      <Grid item xs={12} sm={12} md={6}>
        <TextField
          data-test-id="suppression-create-callerid-field"
          select
          label={t("Caller Id")}
          variant="outlined"
          size="small"
          fullWidth
          name="callerId"
          value={suppressVoice.callerId}
          onChange={onChange}
          error={Boolean(
            errors[SUPPRESSION_TYPE_ENUM.VOICE] &&
              errors[SUPPRESSION_TYPE_ENUM.VOICE]["callerId"]
          )}
          helperText={
            Boolean(
              errors[SUPPRESSION_TYPE_ENUM.VOICE] &&
                errors[SUPPRESSION_TYPE_ENUM.VOICE]["callerId"]
            )
              ? t("must be a valid callerId")
              : ""
          }
          className={globalClasses.filterPaper}
        >
          {callerIds.map((callerId) => (
            <MenuItem key={callerId} value={callerId.toString()}>
              {callerId}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={12} md={6}>
        <PhoneInput
          data-test-id="suppression-create-voice-number-field"
          label={t("Phone Number")}
          country={"us"}
          value={suppressVoice.to}
          name="to"
          onChange={(value) => onChange({ target: { name: "to", value } })}
          variant="outlined"
          size="small"
          fullWidth
          error={Boolean(
            errors[SUPPRESSION_TYPE_ENUM.VOICE] &&
              errors[SUPPRESSION_TYPE_ENUM.VOICE]["to"]
          )}
          helperText={
            Boolean(
              errors[SUPPRESSION_TYPE_ENUM.VOICE] &&
                errors[SUPPRESSION_TYPE_ENUM.VOICE]["to"]
            )
              ? t("must be a valid phone number")
              : ""
          }
        />
      </Grid>
      {/* ECS-2200 Commented out to remove this feature while being investigated */}
      {/*<Grid item xs={12} sm={6} md={4} lg={3} xl={2}>*/}
      {/*  <TextField*/}
      {/*    data-test-id="suppression-upload"*/}
      {/*    size="small"*/}
      {/*    name="toEmail"*/}
      {/*    value={suppressEmail.toEmail}*/}
      {/*    onChange={() => setIsUpload(true)}*/}
      {/*    helperText={*/}
      {/*      Boolean(errors["toEmail"]) ? t("must be a valid Email") : ""*/}
      {/*    }*/}
      {/*    type="checkbox"*/}
      {/*  />{" "}*/}
      {/*  Uploads*/}
      {/*</Grid>*/}
    </>
  );
  return (
    <Page screenSized data-test-id="single-master-template-page">
      <Grid direction="column" spacing={2}>
        <SuppressionToggle
          onTabChange={onInputTabChange(history)}
          activeTab={type}
          errors={""}
          readOnly={false}
        />
      </Grid>

      <Box p={2} className={classes.content}>
        {!isUpload && (
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  color="primary"
                >
                  <Typography gutterBottom variant="h4" align="left">
                    {`Suppress New ${label}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Card>
                    <CardContent>
                      <Grid
                        container
                        spacing={4}
                        justify="space-between"
                        alignItems="center"
                      >
                        {type == SUPPRESSION_TYPE_ENUM.EMAIL
                          ? addEmailToSuppressionList
                          : type == SUPPRESSION_TYPE_ENUM.VOICE
                          ? addVoiceToSuppressionList
                          : addSMSToSuppressionList}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
        {isUpload && <Upload />}
      </Box>
      <Grid
        container
        spacing={2}
        alignItems="center"
        justify="flex-end"
        className={classes.root}
      >
        <Grid item>
          <ErrorButtonContained
            data-test-id="suppression-create-cancel-button"
            startIcon={<DeleteForever />}
            onClick={onCancel}
            disabled={false}
          >
            {t("Cancel")}
          </ErrorButtonContained>
        </Grid>
        <Grid item>
          <PrimaryButtonContained
            data-test-id="suppression-create-save-button"
            startIcon={<Save />}
            onClick={onSave}
            disabled={false}
            error={false}
          >
            {t("Save")}
          </PrimaryButtonContained>
        </Grid>
      </Grid>
    </Page>
  );
}

export default memo(CreateSuppressionNumber);
